import React from 'react';
import { Callout, DocHeading, DocTable, Shot } from './components';

function PageBloodshed({ navigate }) {
  const link = (id, text) => (
    <button onClick={() => navigate(id)} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">{text}</button>
  );
  return (
    <div>
      <h1 className="text-[2.5rem] leading-[1.1] font-semibold text-white tracking-[-0.02em] mb-3">Bloodshed</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        In a bloodshed run, the number of chests you get depends on how fast you finish it. This
        feature does two things: it puts a live counter on the game screen, so you always know how
        many chests you are on track for &mdash; and while a route runs, it collects the chests one
        by one. It is on the <strong className="text-white">FARM &rsaquo; Bloodshed</strong> tab.
      </p>

      <Callout type="info">
        <strong>The switch only shows or hides the timer.</strong> It sits in the title row of the{' '}
        <strong>BLOODSHED</strong> card and is on by default. Waiting for the chests and collecting
        them is done by the <strong>Bloodshed chests</strong> point of your route &mdash; that works
        with the timer off too.
      </Callout>

      <Shot
        name="bloodshed-01-tab.png"
        tab="FARM › Bloodshed"
        caption="Mid-run: 4 chests at this pace, 12:48 before the next one is lost; the last collection opened all 5."
        shoot="menushot (stage bloodshed + bloodgame run): a FARM › Bloodshed lap bloodshed-futás közben — Chests at this pace 4, Until the next one is lost 12:48, Time on this map 47:12, a küszöb-sor, Prediction 8/9 exact; alatta COLLECT THE CHESTS (45 m (fixed), Chests appeared after 1840 ms) és a Last collection (Opened 5)."
      />

      <DocHeading>The Counter on Screen</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        While you are in a bloodshed run, a small box sits at the{' '}
        <strong className="text-white">top centre of the game screen</strong> &mdash; with the menu
        open or closed, and whether you play by hand or a route drives. The big number is the
        chest count; the line under it tells you what comes next, in seconds.
      </p>
      <DocTable
        headers={['What you see', 'What it means']}
        rows={[
          [<><strong className="text-white">4</strong> &nbsp;&nbsp; 768.0 s to 3</>, 'At this pace you get 4 chests. In 768 seconds (12:48) it drops to 3 — this is your cue to hurry.'],
          [<><strong className="text-white">1</strong> &nbsp;&nbsp; last band</>, 'Every threshold has passed. You still get 1 chest; it cannot drop any further.'],
          [<><strong className="text-white">3</strong> &nbsp;&nbsp; done at 4210.4 s (got 3)</>, 'The quest closed at 4210 seconds (70 minutes), which is worth 3 chests, and 3 chests actually appeared. Until the chests show up, the “(got …)” part is missing.'],
        ]}
      />
      <DocTable
        headers={['Colour', 'What it means']}
        rows={[
          [<strong className="text-white">Green</strong>, 'The run is on and the count is safe for now.'],
          [<strong className="text-white">Orange, during the run</strong>, 'You are within about a second of the next threshold. The count can flip a moment early or late here.'],
          [<strong className="text-white">Purple, after the run</strong>, 'The chests that appeared match what the counter said — or they have not been counted yet (no “(got …)” part).'],
          [<strong className="text-white">Orange, after the run</strong>, 'A different number of chests appeared than the counter said.'],
        ]}
      />
      <Callout type="info">
        <strong>The clock starts when you enter the map</strong>, not when you land your first hit,
        and it stops the moment the bloodshed quest closes. Standing around after that never costs
        you a chest.
      </Callout>

      <DocHeading>The BLOODSHED Card</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The same numbers on the tab, in minutes and seconds. You do not need to keep the tab open
        during a run &mdash; the counter on screen is enough.
      </p>
      <DocTable
        headers={['Row', 'What it shows']}
        rows={[
          [<strong className="text-white">Status</strong>, '“not in a bloodshed run” when you are not in one.'],
          [<strong className="text-white">Chests at this pace</strong>, 'How many chests you would get if the quest closed right now. “(about to change)” means you are right at a threshold.'],
          [<strong className="text-white">Until the next one is lost</strong>, 'Time left before the count drops by one. “nothing left to lose” once every threshold has passed.'],
          [<strong className="text-white">Time on this map</strong>, 'How long ago you entered the map.'],
          [<strong className="text-white">Last result / Took</strong>, 'After the quest closes: how many chests the run earned, and how long it took.'],
          [<strong className="text-white">Prediction</strong>, 'Appears after the first finished run: how many runs the counter was exactly right, and how many times it said more (over) or fewer (under) chests than actually appeared. Counted since the bot was started — your own measure of how far to trust the number.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        Under the rows, a line spells out the thresholds. The bot reads them from the game itself;
        at the time of writing they are:
      </p>
      <DocTable
        headers={['Quest closed', 'Chests']}
        rows={[
          ['under 30 minutes', '5'],
          ['30 – 60 minutes', '4'],
          ['60 – 90 minutes', '3'],
          ['90 – 120 minutes', '2'],
          ['after 120 minutes', '1'],
        ]}
      />

      <DocHeading>Collecting the Chests</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The collection belongs to the route: it works <strong className="text-white">only while a
        route runs</strong>, and only on a map whose part of the route has a{' '}
        <strong className="text-white">Bloodshed chests</strong> point (the ready-made{' '}
        <strong className="text-white">Blood chests</strong> run on {link('autopilot', 'AUTOPILOT › Play')}{' '}
        has one). There, whenever it sees bloodshed chests within <strong className="text-white">45 m</strong>{' '}
        that it has not dealt with yet, it holds the route and works through them, nearest first:
      </p>
      <DocTable
        headers={['Step', 'What happens']}
        rows={[
          [<strong className="text-white">1. Walk up</strong>, 'Goes right up to the chest, not a few metres short.'],
          [<strong className="text-white">2. Stand still</strong>, 'Waits until the character has fully stopped before opening. If it never comes to a full stop, it opens after about 3 seconds anyway.'],
          [<strong className="text-white">3. Wait for the team</strong>, 'Only in party mode: waits until the teammates are there too.'],
          [<strong className="text-white">4. Open</strong>, 'Up to three tries per chest.'],
          [<strong className="text-white">5. Check</strong>, 'The chest only counts as opened once the game no longer lets it be opened.'],
          [<strong className="text-white">6. Short pause</strong>, 'Lets the opening finish, then on to the next chest. After the last chest it waits for the loot to land and picks it up.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        At the end of the list it looks again, so chests that appear late are still picked up.
        When nothing is left, the route carries on.
      </p>
      <p className="text-sm text-gray-400 mb-4">
        <strong className="text-white">When a chest is hard to reach:</strong> if it cannot get to a
        chest, it leaves it for now and tries once more a little later; the second failure is final.
        If nothing at all changes for 30 seconds, it counts a{' '}
        <strong className="text-white">stall</strong> and moves past that chest. After two stalls in
        the same bloodshed run it stops collecting until the next run. Fighting and waiting for
        teammates never count as a stall.
      </p>

      <Callout type="warning">
        <strong>Playing by hand, or no point on this map?</strong> Then this collection does
        nothing &mdash; the Status line says &ldquo;the route is not running&rdquo; or &ldquo;no
        bloodshed point on this part of the route&rdquo;. Bloodshed chests are then opened by{' '}
        {link('auto-gather', 'Auto Gather')} like any other chest, if <strong>Bloodshed chest</strong>{' '}
        is ticked in its <strong>Chests</strong> list. Where the route has the point, Auto Gather
        leaves bloodshed chests alone.
      </Callout>
      <Callout type="info">
        <strong>The loot:</strong> after a bloodshed chest, the{' '}
        <strong>Wait for dropped loot</strong> option (in the ADVANCED section of{' '}
        {link('auto-gather', 'FARM › Gather')}) keeps the route standing until what dropped is picked
        up. Bloodshed chests get its full wait, other chests half of it.
      </Callout>
      <Callout type="info">
        <strong>In a party</strong> (<strong>Play as a party</strong> on{' '}
        {link('party', 'AUTOPILOT › Party')}), a chest is not opened until every teammate has
        reached the bloodshed point too. Only if the team cannot come together does{' '}
        <strong>Patience</strong> let it open anyway. In the ADVANCED section of that tab,{' '}
        <strong>Bloodshed: wait for the group bonus</strong> makes every chest also wait until the
        group bonus is active on each teammate.
      </Callout>

      <DocHeading>The COLLECT THE CHESTS Card</DocHeading>
      <DocTable
        headers={['Row', 'What it means']}
        rows={[
          [<strong className="text-white">Status</strong>, 'What the collection is doing, in words — for example “no bloodshed point on this part of the route”, “no bloodshed chest in sight”, “walking to the chest”, “waiting for a full stop”, “waiting for it to open”, “opened - short pause”, “picking up the loot of the last chest”, or “stopped for this run (stalled twice)”.'],
          [<strong className="text-white">Chests the scan sees</strong>, 'For example “5 seen / 5 collectable”. Left: bloodshed chests the bot can see nearby. Right: how many of those it will still collect (not dealt with yet, not waiting for a retry, within range). If the two differ, one of those filters is taking a chest. “SCAN FULL” means there were more objects around than the scan can hold.'],
          [<strong className="text-white">Collect chests within</strong>, <><strong>45 m (fixed)</strong>. The chests of a bloodshed run always spawn within it, so there is nothing to set.</>],
          [<strong className="text-white">Chests appeared after</strong>, 'How long after the quest closed the chests showed up. “not yet, 10 s after the quest closed - still waiting” means they are late; the point keeps waiting for them.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">While it is collecting, more rows appear:</p>
      <DocTable
        headers={['Row', 'What it means']}
        rows={[
          [<strong className="text-white">Chest</strong>, 'Which one of how many it is at, e.g. “2 / 5”.'],
          [<strong className="text-white">Opened</strong>, 'Opened by the bot, and confirmed.'],
          [<strong className="text-white">Already open</strong>, 'The game said it could not be opened when the bot got there — someone else got it.'],
          [<strong className="text-white">Skipped</strong>, 'Given up on: it could not walk to it, or three tries failed.'],
          [<strong className="text-white">Vanished</strong>, 'Left the scan while the bot was working on it. Not counted as opened.'],
          [<strong className="text-white">Stalls / Stalled in</strong>, 'How many times nothing changed for 30 seconds, and in which step it happened.'],
          [<strong className="text-white">Route held</strong>, 'How much longer the route is held for the chests. “no - the run is moving” means the route is not being held.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        When it is done, a <strong className="text-white">Last collection</strong> section keeps the
        result so you can check it later: <strong className="text-white">Opened</strong>,{' '}
        <strong className="text-white">Already open</strong>,{' '}
        <strong className="text-white">Gave up on</strong>,{' '}
        <strong className="text-white">Vanished</strong>,{' '}
        <strong className="text-white">Out of range</strong>,{' '}
        <strong className="text-white">Stalled in</strong> and{' '}
        <strong className="text-white">When</strong>. A chest left behind shows up in exactly one of
        these.
      </p>

      <DocHeading>The Bloodshed Chests Point</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        In the {link('route-builder', 'route editor')}, add a point of type{' '}
        <strong className="text-white">Bloodshed chests</strong> where the chests appear &mdash; after
        the part of the route that kills the bloodshed monsters. The ready-made Blood chests run
        already has it. When the character reaches it:
      </p>
      <DocTable
        headers={['Situation', 'What the run does']}
        rows={[
          ['The quest is still running', 'Stands at the point and waits.'],
          ['The quest has just closed', 'Waits until the chests actually appear (they spawn a moment later), collects them, then carries on.'],
          ['There is nothing to wait for', 'Goes on right away.'],
          ['Nothing shows up', 'Gives up after 60 seconds and carries on. Time spent collecting does not count towards the 60 seconds.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        On {link('autopilot', 'AUTOPILOT › Play')} you will see one of these messages:
      </p>
      <DocTable
        headers={['Message', 'What it means']}
        rows={[
          [<strong className="text-white">Waiting for the bloodshed chests (12 s)...</strong>, 'Standing at the point. The seconds count towards the 60-second limit.'],
          [<strong className="text-white">Collecting the bloodshed chests...</strong>, 'Working through the chests.'],
          [<strong className="text-white">Bloodshed point done - going on.</strong>, 'Done, or there was never anything to collect.'],
          [<strong className="text-white">Bloodshed point: gave up after 60 s - going on.</strong>, 'Nothing appeared in time.'],
        ]}
      />
      <Callout type="warning">
        <strong>No point, no waiting.</strong> On a map whose part of the route has no Bloodshed
        chests point, nothing waits for the chests and nothing works through them one by one &mdash;
        only Auto Gather opens what happens to be in its reach. If your own route walks off before
        the chests appear, add the point.
      </Callout>

      <DocHeading>Why Didn&rsquo;t It&hellip;</DocHeading>
      <DocTable
        headers={['Problem', 'Answer']}
        rows={[
          [<strong className="text-white">It left a chest behind</strong>, 'Read the Last collection section. Out of range: it was further than 45 m from the character. Gave up on: the chest was somewhere it could not walk to. Already open: someone else got it. Vanished: it dropped out of the scan.'],
          [<strong className="text-white">It does not collect anything</strong>, 'A route has to be running, and that map’s part of the route needs a Bloodshed chests point. After two stalls in a run it pauses until the next bloodshed run. The BLOODSHED switch does not matter here.'],
          [<strong className="text-white">Auto Gather opened the chests instead</strong>, 'Normal where the route has no Bloodshed chests point, or when you play by hand.'],
          [<strong className="text-white">The route runs off before the chests appear</strong>, 'Add a Bloodshed chests point to the route.'],
          [<strong className="text-white">The counter was one chest off</strong>, 'Look at the colour: orange means you were within about a second of a threshold, where it can go either way. The Prediction row shows how often it has been right.'],
          [<strong className="text-white">There is no counter on screen</strong>, 'It only appears inside a bloodshed run, and only with the BLOODSHED switch on.'],
        ]}
      />
    </div>
  );
}

export default PageBloodshed;
